/**
 * @module config/countryConfig
 * @private
 */
import { COUNTRY_CODE, CURRENCY_ID } from '../constants';
import type {
  CountryCode,
  CountryCodeStrict,
  LanguageCode,
  CountryConfig,
} from '../typings';
import { getNotchesByCountryAndLanguage } from './notchConfig';

/**
 * @private
 */
const SERVER_NUMBER_CONVERT_RATIO = 100_000;

const COUNTRY_CONF: Record<
  CountryCodeStrict,
  Omit<CountryConfig, 'notches'>
> = {
  [COUNTRY_CODE.SG]: {
    currencies: {
      [CURRENCY_ID.SGD]: {},
    },
    defaultCurrency: CURRENCY_ID.SGD,
  },
  [COUNTRY_CODE.ID]: {
    currencies: {
      [CURRENCY_ID.IDR]: {},
    },
    defaultCurrency: CURRENCY_ID.IDR,
  },
  [COUNTRY_CODE.MY]: {
    currencies: {
      [CURRENCY_ID.MYR]: {},
    },
    defaultCurrency: CURRENCY_ID.MYR,
  },
  [COUNTRY_CODE.PH]: {
    currencies: {
      [CURRENCY_ID.PHP]: {},
    },
    defaultCurrency: CURRENCY_ID.PHP,
  },
  [COUNTRY_CODE.TW]: {
    currencies: {
      [CURRENCY_ID.TWD]: {},
    },
    defaultCurrency: CURRENCY_ID.TWD,
  },
  [COUNTRY_CODE.TH]: {
    currencies: {
      [CURRENCY_ID.THB]: {},
    },
    defaultCurrency: CURRENCY_ID.THB,
  },
  [COUNTRY_CODE.VN]: {
    currencies: {
      [CURRENCY_ID.VND]: {},
    },
    defaultCurrency: CURRENCY_ID.VND,
  },
  [COUNTRY_CODE.IR]: {
    currencies: {
      [CURRENCY_ID.IRR]: {
        convertRate: 10 * SERVER_NUMBER_CONVERT_RATIO,
      },
      [CURRENCY_ID.MMK]: {},
    },
    defaultCurrency: CURRENCY_ID.IRR,
    serverCurrency: CURRENCY_ID.MMK,
  },
  [COUNTRY_CODE.HK]: {
    currencies: {
      [CURRENCY_ID.HKD]: {},
    },
    defaultCurrency: CURRENCY_ID.HKD,
  },
  [COUNTRY_CODE.BR]: {
    currencies: {
      [CURRENCY_ID.BRL]: {},
    },
    defaultCurrency: CURRENCY_ID.BRL,
  },
  [COUNTRY_CODE.CN]: {
    currencies: {
      [CURRENCY_ID.CNY]: {},
    },
    defaultCurrency: CURRENCY_ID.CNY,
  },

  [COUNTRY_CODE.MX]: {
    currencies: {
      [CURRENCY_ID.MXN]: {},
    },
    defaultCurrency: CURRENCY_ID.MXN,
  },
  [COUNTRY_CODE.CO]: {
    currencies: {
      [CURRENCY_ID.COP]: {},
    },
    defaultCurrency: CURRENCY_ID.COP,
  },
  [COUNTRY_CODE.CL]: {
    currencies: {
      [CURRENCY_ID.CLP]: {},
    },
    defaultCurrency: CURRENCY_ID.CLP,
  },
  [COUNTRY_CODE.AR]: {
    currencies: {
      [CURRENCY_ID.ARS]: {},
    },
    defaultCurrency: CURRENCY_ID.ARS,
  },
  [COUNTRY_CODE.PL]: {
    currencies: {
      [CURRENCY_ID.PLN]: {},
    },
    defaultCurrency: CURRENCY_ID.PLN,
  },
  [COUNTRY_CODE.ES]: {
    currencies: {
      [CURRENCY_ID.EUR]: {},
    },
    defaultCurrency: CURRENCY_ID.EUR,
  },
  [COUNTRY_CODE.FR]: {
    currencies: {
      [CURRENCY_ID.EUR]: {
        separator: ' ',
      },
    },
    defaultCurrency: CURRENCY_ID.EUR,
  },
  [COUNTRY_CODE.IN]: {
    currencies: {
      [CURRENCY_ID.INR]: {},
    },
    defaultCurrency: CURRENCY_ID.INR,
  },
  [COUNTRY_CODE.US]: {
    currencies: {
      [CURRENCY_ID.USD]: {},
    },
    defaultCurrency: CURRENCY_ID.USD,
  },
  [COUNTRY_CODE.KR]: {
    currencies: {
      [CURRENCY_ID.KRW]: {},
    },
    defaultCurrency: CURRENCY_ID.KRW,
  },
  [COUNTRY_CODE.JP]: {
    currencies: {
      [CURRENCY_ID.JPY]: {},
    },
    defaultCurrency: CURRENCY_ID.JPY,
  },
  [COUNTRY_CODE.KH]: {
    currencies: {
      [CURRENCY_ID.KHR]: {},
    },
    defaultCurrency: CURRENCY_ID.KHR,
  },
  [COUNTRY_CODE.MM]: {
    currencies: {
      [CURRENCY_ID.MMK]: {},
    },
    defaultCurrency: CURRENCY_ID.MMK,
  },
  [COUNTRY_CODE.LA]: {
    currencies: {
      [CURRENCY_ID.LAK]: {},
    },
    defaultCurrency: CURRENCY_ID.LAK,
  },
} as const;

export function getCountryConfig(
  country: CountryCode,
  language?: LanguageCode
): CountryConfig {
  const countryCode: CountryCodeStrict =
    typeof country === 'string'
      ? (country.toUpperCase() as CountryCodeStrict)
      : (country as CountryCodeStrict);
  const notches = getNotchesByCountryAndLanguage(countryCode, language);

  return {
    ...COUNTRY_CONF[countryCode],
    notches,
  } as CountryConfig;
}
